import { useEffect, useState, useMemo } from 'react'
import { useAuth } from '../components/AuthProvider'
import { API_ENDPOINTS } from '../config/api'
import { Wifi, Mail, FileUp, CheckSquare, RefreshCw } from 'lucide-react'
import { PageLayout } from '../layouts/PageLayout'
import { Button } from '../components/ui/Button'
import { TextField } from '../components/ui'
import { Select } from '../components/ui/Select'
import { useI18n } from '../i18n'
import { GovernanceListingSection } from '../components/governance/GovernanceListingSection'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import styles from '@/styles/pages/Notifications.module.scss'

export default function Notifications() {
  const { t } = useI18n()
  const { token, user } = useAuth()
  const [items, setItems] = useState<any[]>([])
  const [orgId, setOrgId] = useState('')
  const [type, setType] = useState<string>('')
  const [uploadingFor, setUploadingFor] = useState<string | null>(null)
  const [fileByNotif, setFileByNotif] = useState<Record<string, File | null>>({})

  const isAdmin = (user?.role || '').toLowerCase() === 'admin'

  const typeOptions = useMemo(
    () => [
      { value: '', label: t('notifications.filter.allTypes', 'All types') },
      { value: 'scan_request', label: t('notifications.type.scanRequest', 'Scan request') },
      { value: 'message', label: t('notifications.type.message', 'Message') },
    ],
    [t],
  )

  const load = async () => {
    try {
      const url = new URL(API_ENDPOINTS.NOTIFICATIONS_LIST, window.location.origin)
      if (orgId) url.searchParams.set('organization_id', orgId)
      if (type) url.searchParams.set('type', type)
      const res = await fetch(url.toString().replace(window.location.origin, ''), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        const data = await res.json()
        setItems(Array.isArray(data.notifications) ? data.notifications : [])
      }
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    if (isAdmin) {
      void load()
    }
  }, [token, orgId, type, isAdmin])

  if (!isAdmin) {
    return (
      <PageLayout pageScope="notifications-page" title={t('nav.notifications', 'Notifications')}>
        <p className={styles.forbidden}>{t('notifications.forbidden', 'Forbidden')}</p>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      pageScope="notifications-page"
      title={t('nav.notifications', 'Notifications')}
      description={t('notifications.description', 'Review and process organization notifications')}
      actions={
        <Button type="button" color="outline" icon={<RefreshCw className={styles.refreshIcon} aria-hidden />} onClick={() => void load()}>
          {t('common.refresh', 'Refresh')}
        </Button>
      }
    >
      <div className={listStyles.pageStack}>
        <GovernanceListingSection
          title={t('notifications.listTitle', 'Notifications')}
          description={t('notifications.listSubtitle', 'Filter by organization or type, then upload reports or mark items complete.')}
          toolbar={
            <div className={listStyles.panelToolbarFull}>
              <div className={listStyles.filtersRow}>
                <div className={`${listStyles.toolbarField} ${listStyles.filtersToolbarGrow}`}>
                  <TextField
                    id="notif-org"
                    label={t('notifications.filter.organizationId', 'Organization ID')}
                    value={orgId}
                    onChange={setOrgId}
                    placeholder={t('notifications.filter.organizationPlaceholder', 'Optional filter')}
                    minWidth="100%"
                  />
                </div>
                <div className={listStyles.toolbarField}>
                  <Select<string>
                    label={t('notifications.filter.type', 'Type')}
                    options={typeOptions}
                    value={type}
                    onChange={(v) => setType(v ?? '')}
                    minWidth="12rem"
                  />
                </div>
              </div>
            </div>
          }
        >
          {items.length === 0 ? (
            <p className={listStyles.emptyState}>{t('notifications.empty', 'No notifications')}</p>
          ) : (
            <ul className={styles.notifList}>
              {items.map((n) => (
                <li
                  key={n.id}
                  className={`${styles.notifItem} ${n.status === 'completed' ? styles.notifItemCompleted : ''}`.trim()}
                >
                  <div className={styles.notifRow}>
                    <div className={styles.notifMain}>
                      <div className={styles.notifMetaRow}>
                        {n.type === 'scan_request' ? (
                          <Wifi className={styles.iconScan} aria-hidden />
                        ) : (
                          <Mail className={styles.iconMessage} aria-hidden />
                        )}
                        <span className={styles.notifId}>ID: {n.id}</span>
                        <span className={styles.typePill}>
                          {n.type === 'scan_request'
                            ? t('notifications.type.scanRequest', 'Scan request')
                            : t('notifications.type.message', 'Message')}
                        </span>
                        <span
                          className={
                            n.status === 'completed'
                              ? `${styles.statusPill} ${styles.statusCompleted}`
                              : n.status === 'scan_started'
                                ? `${styles.statusPill} ${styles.statusScanStarted}`
                                : `${styles.statusPill} ${styles.statusDefault}`
                          }
                        >
                          {n.status}
                        </span>
                      </div>
                      <div className={styles.textBody}>
                        {t('notifications.organization', 'Organization')}:{' '}
                        <span className={styles.fontMedium}>{n.organizationName || n.organizationId}</span>
                      </div>
                      {Array.isArray(n.assets) && n.assets.length > 0 && (
                        <div className={styles.textMuted}>
                          {t('notifications.assets', 'Assets')}: {n.assets.join(', ')}
                        </div>
                      )}
                      {Array.isArray(n.manualResources) && n.manualResources.length > 0 && (
                        <div className={styles.textMuted}>
                          {t('notifications.manualResources', 'Manual resources')}:{' '}
                          {n.manualResources.map((r: any) => `${r.host}:${r.port || ''}`).join(', ')}
                        </div>
                      )}
                      {n.message && <div className={styles.textBody}>{n.message}</div>}
                      <div className={styles.textXs}>
                        {t('notifications.created', 'Created')}: {new Date(n.createdAt).toLocaleString()}
                      </div>
                      {n.completedAt && (
                        <div className={styles.textXs}>
                          {t('notifications.completed', 'Completed')}: {new Date(n.completedAt).toLocaleString()}{' '}
                          {n.completedByAdminId != null && `(admin ${n.completedByAdminId})`}
                        </div>
                      )}
                    </div>

                    {n.type === 'scan_request' && (
                      <div className={styles.actionsCol}>
                        <div className={styles.uploadRow}>
                          <label className={styles.uploadLabel}>{t('notifications.attachReport', 'Attach report (CSV/PDF/Excel)')}</label>
                          <input
                            type="file"
                            accept=".csv,application/pdf,.pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx"
                            className={styles.fileInput}
                            onChange={(e) => {
                              const f = (e.target as HTMLInputElement).files?.[0] || null
                              setFileByNotif((prev) => ({ ...prev, [n.id]: f }))
                            }}
                          />
                          <Button
                            type="button"
                            size="small"
                            color="primary"
                            disabled={!fileByNotif[n.id] || n.status === 'completed' || uploadingFor === n.id}
                            icon={<FileUp className={styles.refreshIcon} aria-hidden />}
                            onClick={async () => {
                              if (!fileByNotif[n.id]) return
                              setUploadingFor(n.id)
                              try {
                                const fd = new FormData()
                                fd.append('notification_id', n.id)
                                fd.append('file', fileByNotif[n.id] as File)
                                const res = await fetch(API_ENDPOINTS.NOTIFICATION_UPLOAD_REPORT, {
                                  method: 'POST',
                                  headers: { Authorization: `Bearer ${token}` },
                                  body: fd,
                                })
                                if (res.ok) await load()
                                else alert(t('notifications.uploadFailed', 'Upload failed'))
                              } finally {
                                setUploadingFor(null)
                              }
                            }}
                          >
                            {t('notifications.uploadReport', 'Upload report')}
                          </Button>
                        </div>
                        <Button
                          type="button"
                          size="small"
                          color="secondary"
                          disabled={n.status === 'completed'}
                          icon={<CheckSquare className={styles.refreshIcon} aria-hidden />}
                          onClick={async () => {
                            const res = await fetch(API_ENDPOINTS.NOTIFICATION_COMPLETE, {
                              method: 'POST',
                              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ notification_id: n.id }),
                            })
                            if (res.ok) void load()
                          }}
                        >
                          {t('notifications.markCompleted', 'Mark as completed')}
                        </Button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </GovernanceListingSection>
      </div>
    </PageLayout>
  )
}
